import ObexRequestHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import { buildQuery, getErrorMessage, unwrapObject, unwrapList, getAccessToken } from './apiUtils';
import { CartItem } from '../types/book.types';
import { Transaction } from '../types/transaction.types';
import { getTerminalToken } from './TerminalService';

/**
 * No hay endpoint de venta en el backend: una venta se modela como factura.
 * La cadena es invoices_create -> invoice_lines_add (una por linea) ->
 * invoices_issue -> invoices_collect, que ademas genera el asiento contable.
 *
 * Al no ser atomico, un fallo a mitad deja la factura a medias (creada sin
 * emitir, o emitida sin cobrar). Lo suyo seria un endpoint unico en el backend
 * que lo haga todo en una transaccion.
 */
const ROUTES = {
    createInvoice: '/invoices_create',
    addLine: '/invoice_lines_add',
    issueInvoice: '/invoices_issue',
    collectInvoice: '/invoices_collect',
    getInvoice: '/invoices_get',
    listInvoices: '/invoices_list',
    refundable: '/invoices_refundable'
};

export const TAX_RATE = 4;

/**
 * invoices.source_process_type estaba sin usar en todo el backend (no la
 * escribia ni la leia ningun .avap). Se aprovecha para guardar el id de la
 * factura que rectifica el abono, en crudo: la cabecera de invoices no tiene
 * columna de descripcion, y payment_instrument_ref ya lleva el metodo de pago
 * -- ademas invoices_collect lo sobrescribe al cobrar.
 */
const isRefundRef = (raw: any): boolean => /^\d+$/.test(String(raw || '').trim());

/**
 * Los cuatro endpoints estan registrados como POST, pero el gateway enruta por
 * path ignorando el metodo y NO acepta POST desde el navegador: sin cuerpo
 * responde 400 y con cuerpo (lo que manda axios siempre, aunque sea {}) casca
 * con un 500 en HTML. Por GET con los parametros en query string funciona, que
 * ademas es como los .avap los leen (addParam). Mismo problema latente en
 * ClientService.createClient, que sigue usando POST.
 *
 * Ademas rompen la convencion de los .avap (cuerpo string = error): devuelven
 * un string tambien cuando van bien, asi que getErrorMessage los da por
 * fallidos y hay que distinguirlos a mano.
 */
const SUCCESS_BODIES = ['Success', 'Factura cobrada exitosamente', 'Factura enviada por email exitosamente'];

export const call = async (path: string, params: Record<string, any>): Promise<any> => {
    const res = await ObexRequestHandlerP80.get(`${path}${buildQuery(params)}`);
    const error = getErrorMessage(res);
    if (error && SUCCESS_BODIES.indexOf(error) === -1) throw new Error(error);
    return res;
};

/** Devuelve el id de la factura emitida y cobrada. */
export const registerSale = async (
    items: CartItem[],
    total: number,
    clientId: string,
    paymentMethod: string,
    promoPct = 0,
    ticketId?: string
): Promise<string> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    // Los precios del catalogo ya llevan IVA incluido, asi que la base se
    // extrae del total en vez de sumarle el impuesto por encima.
    const subtotal = total / (1 + TAX_RATE / 100);
    const taxAmount = total - subtotal;

    const createRes = await call(ROUTES.createInvoice, {
        access_token: accessToken,
        // La sociedad la pone el servidor a partir de la tienda del terminal.
        terminal_token: getTerminalToken(),
        client_id: clientId,
        invoice_number: `TPV-${Date.now()}`,
        subtotal: subtotal.toFixed(2),
        tax_amount: taxAmount.toFixed(2),
        total: total.toFixed(2),
        status: 'draft',
        currency: 'EUR',
        ticket_id: ticketId
    });

    const invoiceId = unwrapObject(createRes) && unwrapObject(createRes).id;
    if (!invoiceId) throw new Error('El backend no devolvio el id de la factura.');

    // invoices no tiene columna de descuento en la cabecera, asi que la promo
    // del carrito hay que repercutirla en las lineas. Aplicarla a cada linea
    // despues de su propio descuento da el mismo total que useCartTotals, que
    // la calcula sobre (subtotal - descuentos de linea).
    for (const item of items) {
        const factor = (1 - (item.discount || 0) / 100) * (1 - promoPct / 100);
        await call(ROUTES.addLine, {
            access_token: accessToken,
            invoice_id: invoiceId,
            description: item.title,
            quantity: item.quantity,
            unit_price: item.price.toFixed(2),
            discount_pct: (100 * (1 - factor)).toFixed(2),
            tax_rate: TAX_RATE,
            line_total: (item.price * item.quantity * factor).toFixed(2)
        });
    }

    await call(ROUTES.issueInvoice, { access_token: accessToken, id: invoiceId });

    await call(ROUTES.collectInvoice, {
        access_token: accessToken,
        id: invoiceId,
        payment_instrument_ref: paymentMethod
    });

    // La factura recien emitida tiene que salir ya en la busqueda por numero.
    invoiceListCache = null;

    return String(invoiceId);
};

/**
 * Un abono es la MISMA factura que una venta con los importes en negativo:
 * misma serie de numeracion, mismo estado, misma cadena create -> lines ->
 * issue -> collect. No hay tipo de documento aparte.
 *
 * `items` y `total` llegan en positivo (es lo que calcula el carrito de
 * devolucion) y se niegan aqui.
 *
 * El signo se concentra en los importes: `quantity` y `unit_price` se quedan
 * positivos porque mapLine usa `quantity` como tope de lo devolvible, y una
 * cantidad negativa lo romperia. Contablemente cuadra igual.
 */
export const registerRefund = async (
    originalInvoiceId: string,
    items: CartItem[],
    total: number,
    clientId: string,
    paymentMethod: string,
    promoPct = 0,
    ticketId?: string
): Promise<string> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const subtotal = total / (1 + TAX_RATE / 100);
    const taxAmount = total - subtotal;

    const createRes = await call(ROUTES.createInvoice, {
        access_token: accessToken,
        // La sociedad la pone el servidor a partir de la tienda del terminal.
        terminal_token: getTerminalToken(),
        client_id: clientId,
        invoice_number: `TPV-${Date.now()}`,
        subtotal: (-subtotal).toFixed(2),
        tax_amount: (-taxAmount).toFixed(2),
        total: (-total).toFixed(2),
        status: 'draft',
        currency: 'EUR',
        source_process_type: originalInvoiceId,
        ticket_id: ticketId
    });

    const invoiceId = unwrapObject(createRes) && unwrapObject(createRes).id;
    if (!invoiceId) throw new Error('El backend no devolvio el id del abono.');

    for (const item of items) {
        const factor = (1 - (item.discount || 0) / 100) * (1 - promoPct / 100);
        await call(ROUTES.addLine, {
            access_token: accessToken,
            invoice_id: invoiceId,
            description: item.title,
            quantity: item.quantity,
            unit_price: item.price.toFixed(2),
            discount_pct: (100 * (1 - factor)).toFixed(2),
            tax_rate: TAX_RATE,
            line_total: (-item.price * item.quantity * factor).toFixed(2)
        });
    }

    await call(ROUTES.issueInvoice, { access_token: accessToken, id: invoiceId });

    await call(ROUTES.collectInvoice, {
        access_token: accessToken,
        id: invoiceId,
        payment_instrument_ref: paymentMethod
    });

    // El abono tiene que salir ya en la busqueda por numero.
    invoiceListCache = null;

    return String(invoiceId);
};

export const toNumber = (raw: any, fallback = 0): number => {
    const value = Number(raw);
    return isNaN(value) ? fallback : value;
};

/**
 * invoices.payment_instrument_ref guarda lo que mando registerSale, que ya es
 * uno de los metodos del TPV. Si la factura no se cobro por caja (o viene de
 * otro sitio) se da por cobro a factura.
 */
const PAYMENT_METHODS = ['cash', 'card', 'marcial_card', 'gift_card', 'invoice'];

const mapPaymentMethod = (raw: any): Transaction['paymentMethod'] => {
    const ref = String(raw || '').toLowerCase();
    return PAYMENT_METHODS.indexOf(ref) === -1
        ? 'invoice'
        : (ref as Transaction['paymentMethod']);
};

/**
 * invoice_lines no guarda referencia al libro: solo description, quantity,
 * unit_price y discount_pct. El resto del CartItem va con valores neutros; en
 * devoluciones no se mira stock (hasStockFor solo corre con view === 'sale')
 * ni peso.
 *
 * `quantity` es la COMPRADA: es el tope de lo que se puede devolver. El carrito
 * de devolucion arranca sus lineas a 0 aparte.
 */
export const mapLine = (raw: any): CartItem => ({
    id: String(raw.id),
    title: raw.description || 'Linea sin descripcion',
    author: '',
    price: toNumber(raw.unit_price),
    weight: 0,
    isbn: '',
    category: '',
    stock: 0,
    quantity: toNumber(raw.quantity, 1),
    discount: toNumber(raw.discount_pct),
    publisher: ''
});

const mapInvoice = (raw: any): Transaction => {
    const sourceRef = String(raw.source_process_type || '').trim();
    const isRefund = isRefundRef(sourceRef);

    return {
        id: String(raw.id),
        invoiceId: String(raw.id),
        timestamp: new Date(raw.issue_date || raw.created_at || Date.now()),
        items: Array.isArray(raw.lines) ? raw.lines.map(mapLine) : [],
        total: Math.abs(toNumber(raw.total)),
        paymentMethod: mapPaymentMethod(raw.payment_instrument_ref),
        type: isRefund ? 'return' : 'sale',
        refundsInvoiceId: isRefund ? sourceRef : undefined,
        clientId: raw.client_id === undefined || raw.client_id === null
            ? undefined
            : String(raw.client_id),
        clientName: raw.client_name || undefined
    };
};

/** Por debajo de un centimo no queda nada que devolver. */
export const REFUND_TOLERANCE = 0.01;

/**
 * Hasta cuanto puede pasarse una devolucion del disponible por pura deriva de
 * redondeo, antes de darla por intento de devolver de mas.
 *
 * El total de la factura se redondea UNA vez al crearla; el de cada abono se
 * redondea por separado. Sumar cinco abonos redondeados no da lo mismo que
 * redondear la suma: cada uno aporta hasta medio centimo. Devolviendo linea a
 * linea, el ultimo abono se pasa por uno o dos centimos en una de cada ocho
 * ventas (medido sobre 200.000 simuladas).
 *
 * 0.05 cubre hasta diez abonos sobre la misma factura. Por encima de eso ya no
 * es redondeo, es que alguien pide de mas, y lo rechaza el backend.
 */
export const REFUND_ROUNDING_MARGIN = 0.05;

export interface InvoiceRefundStatus {
    /** Total de la factura original, en positivo. */
    total: number;
    /** Suma de los abonos ya emitidos sobre ella, en positivo. */
    refunded: number;
    /** Lo que queda por devolver. Nunca negativo. */
    available: number;
    /** La factura llego a cobrarse. Sin esto no hay nada que devolver. */
    isPaid: boolean;
}

/** Postgres manda los booleanos como true/false, pero segun el driver llegan como 't'/'true'. */
const toBool = (raw: any): boolean =>
    raw === true || raw === 't' || raw === 'true';

/**
 * Cuanto queda vivo de una factura: su total menos los abonos que la
 * rectifican. La cuenta la hace entera /invoices_refundable, que suma los
 * abonos por invoices.source_process_type = <id de la factura>.
 *
 * Que viva en el backend importa mas de lo que parece: antes se calculaba aqui
 * sobre /invoices_list, que devuelve TODAS las facturas y va cacheado, asi que
 * el tope podia salir de una foto vieja. Contra el endpoint cada consulta es
 * del momento, y el criterio de que abonos cuentan esta en un solo sitio.
 *
 * Esto es para PINTAR el tope. Quien autoriza una devolucion concreta es
 * assertRefundable.
 */
export const getInvoiceRefundStatus = async (invoiceId: string): Promise<InvoiceRefundStatus> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const res = await call(ROUTES.refundable, { access_token: accessToken, id: invoiceId });
    const row = unwrapObject(res) || {};
    console.log(row)
    return {
        total: Math.abs(toNumber(row.total)),
        refunded: Math.abs(toNumber(row.refunded_amount)),
        available: Math.max(0, toNumber(row.refundable_amount)),
        isPaid: toBool(row.is_paid)
    };
};

/**
 * Pregunta al backend si se pueden devolver `amount` euros de la factura.
 * Vuelve sin mas si si; lanza con el mensaje del endpoint si no (422 con el
 * disponible, o 400 si el importe es cero).
 *
 * La comparacion se hace alli y no aqui a proposito: entre leer el disponible
 * y crear el abono no hay hueco donde se cuele otra caja.
 */
const assertRefundable = async (invoiceId: string, amount: number): Promise<void> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    await call(ROUTES.refundable, {
        access_token: accessToken,
        id: invoiceId,
        amount: amount.toFixed(2)
    });
};

/**
 * Cuanto se va a devolver de verdad, ya autorizado por el backend. Devuelve el
 * importe a abonar; lanza con el mensaje del endpoint si no se puede.
 *
 * Si lo pedido se pasa del disponible por menos de REFUND_ROUNDING_MARGIN, se
 * ajusta a lo que queda vivo en vez de rechazarlo: es el ultimo abono de una
 * devolucion por partes, y lo que sobra son centimos de redondeo, no dinero.
 * El cliente recibe exactamente el saldo de la factura.
 *
 * Si se pasa de mas, se manda lo pedido TAL CUAL y que lo rechace el backend:
 * asi el mensaje que ve el cajero es el suyo, con el disponible de verdad, y
 * aqui no se inventa ningun criterio propio.
 */
export const resolveRefundAmount = async (invoiceId: string, requested: number): Promise<number> => {
    const { available } = await getInvoiceRefundStatus(invoiceId);

    const excess = requested - available;
    const isRounding = excess > 0 && excess <= REFUND_ROUNDING_MARGIN && available >= REFUND_TOLERANCE;
    const amount = isRounding ? available : requested;

    await assertRefundable(invoiceId, amount);

    return amount;
};

/**
 * Busca una factura por id, para el numpad de devoluciones. /invoices_get
 * devuelve la cabecera con sus lineas dentro (`lines`), asi que no hace falta
 * pasar tambien por /invoice_lines_list.
 *
 * El id tiene que ser numerico: invoices/get.avap interpola `WHERE i.id = %s`
 * sin comillas, asi que una referencia con letras (un ISBN escaneado, por
 * ejemplo) no daria 404 sino un error de SQL.
 *
 * undefined = no existe. Error = sesion, red, o factura no devolvible.
 */
export const findInvoiceById = async (id: string): Promise<Transaction | undefined> => {
    const cleanId = id.trim();
    if (!/^\d+$/.test(cleanId)) return undefined;

    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    let res: any;
    try {
        res = await call(ROUTES.getInvoice, { access_token: accessToken, id: cleanId });
    } catch (err) {
        // El 404 llega como string en el cuerpo, igual que el resto de errores
        // de los .avap; call() no lo distingue de un fallo de verdad.
        if (String(err?.message).toLowerCase().indexOf('no encontrada') !== -1) return undefined;
        throw err;
    }

    const invoice = unwrapObject(res);
    if (!invoice || !invoice.id) return undefined;

    // Un borrador no se ha cobrado nunca: no hay nada que devolver.
    if (String(invoice.status) === 'draft') {
        throw new Error(`La factura ${cleanId} aun no esta emitida.`);
    }

    const mapped = mapInvoice(invoice);

    // Un abono ya es una devolucion: cargarlo en el numpad dejaria devolver una
    // devolucion, con lineas en negativo sobre negativo.
    if (mapped.type === 'return') {
        throw new Error(`La factura ${cleanId} es un abono: no se puede devolver.`);
    }

    // Lo que no da nada que devolver se corta al cargar la factura, no al
    // confirmar: asi el cajero no monta el carrito para nada.
    const { available, isPaid } = await getInvoiceRefundStatus(cleanId);
    if (!isPaid) {
        throw new Error(`La factura ${cleanId} no esta cobrada: no hay nada que devolver.`);
    }
    // El umbral es el margen de redondeo, no un centimo: devolviendo linea a
    // linea la suma de los abonos puede quedarse a uno o dos centimos del total
    // sin que sobre nada de verdad. Un saldo por debajo de ese margen no es
    // dinero, es residuo, y la factura esta saldada.
    if (available < REFUND_ROUNDING_MARGIN) {
        throw new Error(`La factura ${cleanId} ya se ha devuelto por completo.`);
    }

    return { ...mapped, refundableAmount: available };
};

/**
 * El listado se cachea unos segundos porque el buscador dispara una consulta por
 * tecla y /invoices_list no filtra ni pagina: devuelve TODAS las facturas
 * (SELECT * FROM invoices ORDER BY id DESC). Lo suyo seria un parametro `search`
 * en el backend; mientras no exista, se filtra en cliente.
 */
const INVOICE_LIST_TTL_MS = 30000;
let invoiceListCache: { at: number; paramsKey: string; rows: any[] } | null = null;

const listInvoices = async (forceRefresh = false, page = 1, limit = 500, day?: string): Promise<any[]> => {
    const paramsKey = `${page}-${limit}-${day || ''}`;
    
    if (!forceRefresh && invoiceListCache && invoiceListCache.paramsKey === paramsKey && Date.now() - invoiceListCache.at < INVOICE_LIST_TTL_MS) {
        return invoiceListCache.rows;
    }

    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const params: Record<string, any> = { access_token: accessToken, page, limit };
    if (day) params.day = day;

    const res = await call(ROUTES.listInvoices, params);
    const rows = unwrapList(res);
    invoiceListCache = { at: Date.now(), paramsKey, rows };
    return rows;
};

export const getInvoiceHistory = async (forceRefresh = false): Promise<Transaction[]> => {
    const today = new Date();
    const day = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const rows = await listInvoices(forceRefresh, 1, 500, day);
    return rows.map(mapInvoice);
};

const normalizeRef = (value: any): string => String(value || '').trim().toUpperCase();

/**
 * Id interno a partir del numero de factura (invoices.invoice_number, el
 * "TPV-1789035801695" que ve el cajero). Primero coincidencia exacta y, si no,
 * parcial: el buscador va escribiendo y conviene que encuentre a medio numero.
 */
const findInvoiceIdByNumber = async (ref: string): Promise<string | undefined> => {
    const target = normalizeRef(ref);
    if (!target) return undefined;

    const rows = await listInvoices();
    const exact = rows.find(row => normalizeRef(row.invoice_number) === target);
    const match = exact || rows.find(row => normalizeRef(row.invoice_number).indexOf(target) !== -1);

    return match ? String(match.id) : undefined;
};

/**
 * Busca una factura por lo que sea que haya tecleado el cajero: el id interno
 * (invoices.id, lo que teclea el numpad) o el numero de factura
 * (invoice_number). Es la entrada que usan el numpad y el buscador.
 */
export const findInvoice = async (ref: string): Promise<Transaction | undefined> => {
    const cleanRef = ref.trim();
    if (!cleanRef) return undefined;

    // Todo digitos: lo mas probable es que sea el id interno. Si no existe, se
    // sigue probando como numero de factura (los hay solo numericos).
    if (/^\d+$/.test(cleanRef)) {
        const byId = await findInvoiceById(cleanRef);
        if (byId) return byId;
    }

    const id = await findInvoiceIdByNumber(cleanRef);
    if (!id) return undefined;

    return findInvoiceById(id);
};
